import { Anchor, Breadcrumbs, Card, Group, SegmentedControl, Stack, Text, Tooltip } from '@mantine/core';
import type { AdPlatform } from '@marketing-tool/shared';
import { IconChevronRight } from '@tabler/icons-react';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router';

import { PageWide } from '../../components/PageLayout';
import type { AdGroup, Campaign } from '../../data/game-report/types';
import type { Game } from '../../data/games';

import { AdGroupsTabContent } from './AdGroupsTabContent';
import { AdsTabContent } from './AdsTabContent';
import { CampaignsTabContent } from './CampaignsTabContent';
import { PlatformSelector } from './PlatformSelector';

type TabId = 'campaigns' | 'adGroups' | 'ads';

export const CampaignManagementTab = () => {
    const { game, gameId } = useOutletContext<{ game: Game; gameId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // --- URL state ---

    const platformFromUrl = searchParams.get('platform') as AdPlatform | null;
    const campaignIdFromUrl = searchParams.get('campaignId');
    const adGroupIdFromUrl = searchParams.get('adGroupId');

    const platform = useMemo(() => {
        if (platformFromUrl && game?.platforms.includes(platformFromUrl)) return platformFromUrl;
        return game?.platforms[0];
    }, [platformFromUrl, game?.platforms]);

    const updateUrlParams = useCallback(
        (updates: {
            platform?: AdPlatform;
            campaignId?: string | number | null;
            adGroupId?: string | number | null;
        }) => {
            const params = new URLSearchParams(searchParams);
            if (updates.platform !== undefined) params.set('platform', updates.platform);
            if (updates.campaignId === null) {
                params.delete('campaignId');
                params.delete('adGroupId');
            } else if (updates.campaignId !== undefined) {
                params.set('campaignId', updates.campaignId.toString());
            }
            if (updates.adGroupId === null) {
                params.delete('adGroupId');
            } else if (updates.adGroupId !== undefined) {
                params.set('adGroupId', updates.adGroupId.toString());
            }
            void navigate(`/games/${gameId}/campaigns?${params.toString()}`, { replace: true });
        },
        [searchParams, navigate, gameId],
    );

    // --- URL helpers ---

    const getCampaignUrl = useCallback(
        (campaignId: string | number) => {
            const params = new URLSearchParams(searchParams);
            params.set('campaignId', campaignId.toString());
            params.delete('adGroupId');
            return `/games/${gameId}/campaigns?${params.toString()}`;
        },
        [searchParams, gameId],
    );

    const getAdGroupUrl = useCallback(
        (adGroupId: string | number) => {
            const params = new URLSearchParams(searchParams);
            params.set('adGroupId', adGroupId.toString());
            return `/games/${gameId}/campaigns?${params.toString()}`;
        },
        [searchParams, gameId],
    );

    // --- Data state (for breadcrumbs) ---

    const [campaignsData, setCampaignsData] = useState<Campaign[]>([]);
    const [adGroupsData, setAdGroupsData] = useState<AdGroup[]>([]);

    const selectedCampaign = useMemo(() => {
        if (!campaignIdFromUrl) return undefined;
        return campaignsData.find((c) => c.id.toString() === campaignIdFromUrl);
    }, [campaignIdFromUrl, campaignsData]);

    const selectedAdGroup = useMemo(() => {
        if (!adGroupIdFromUrl) return undefined;
        return adGroupsData.find((ag) => ag.id.toString() === adGroupIdFromUrl);
    }, [adGroupIdFromUrl, adGroupsData]);

    // --- Tab state ---

    const inferredTab: TabId = useMemo(() => {
        if (adGroupIdFromUrl) return 'ads';
        if (campaignIdFromUrl) return 'adGroups';
        return 'campaigns';
    }, [adGroupIdFromUrl, campaignIdFromUrl]);

    const [activeTab, setActiveTab] = useState<TabId>(inferredTab);
    useEffect(() => setActiveTab(inferredTab), [inferredTab]);

    const [visitedTabs, setVisitedTabs] = useState<Set<TabId>>(() => {
        const initial: TabId = adGroupIdFromUrl ? 'ads' : campaignIdFromUrl ? 'adGroups' : 'campaigns';
        const tabs = new Set<TabId>(['campaigns']);
        if (initial === 'adGroups' || initial === 'ads') tabs.add('adGroups');
        if (initial === 'ads') tabs.add('ads');
        return tabs;
    });

    const markTabVisited = (tabId: TabId) => setVisitedTabs((prev) => new Set([...prev, tabId]));

    // --- Navigation handlers ---

    const handlePlatformChange = (newPlatform: AdPlatform) => {
        updateUrlParams({ platform: newPlatform, campaignId: null, adGroupId: null });
    };

    const handleCampaignClick = (campaign: Campaign) => {
        markTabVisited('adGroups');
        updateUrlParams({ campaignId: campaign.id, adGroupId: null });
    };

    const handleAdGroupClick = (adGroup: AdGroup) => {
        markTabVisited('ads');
        updateUrlParams({ adGroupId: adGroup.id });
    };

    const handleTabChange = (newValue: string) => {
        markTabVisited(newValue as TabId);
        setActiveTab(newValue as TabId);
    };

    // --- Navigation callbacks for child breadcrumbs ---

    const handleNavigateToCampaigns = useCallback(() => {
        updateUrlParams({ campaignId: null, adGroupId: null });
    }, [updateUrlParams]);

    const handleNavigateToCampaign = useCallback(() => {
        updateUrlParams({ adGroupId: null });
    }, [updateUrlParams]);

    // --- Render ---

    return (
        <PageWide>
            <Stack gap="lg">
                <Card withBorder>
                    {selectedCampaign && (
                        <Card.Section inheritPadding py="sm">
                            <Breadcrumbs separator={<IconChevronRight size={14} />}>
                                <Anchor
                                    size="sm"
                                    underline="hover"
                                    onClick={handleNavigateToCampaigns}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Campaigns
                                </Anchor>
                                {selectedAdGroup ? (
                                    <>
                                        <Tooltip
                                            label={selectedCampaign.name}
                                            disabled={selectedCampaign.name.length < 20}
                                        >
                                            <Anchor
                                                size="sm"
                                                underline="hover"
                                                onClick={handleNavigateToCampaign}
                                                style={{ cursor: 'pointer' }}
                                                truncate
                                                maw={200}
                                            >
                                                {selectedCampaign.name}
                                            </Anchor>
                                        </Tooltip>
                                        <Tooltip
                                            label={selectedAdGroup.name}
                                            disabled={selectedAdGroup.name.length < 20}
                                        >
                                            <Text size="sm" fw={600} truncate maw={200}>
                                                {selectedAdGroup.name}
                                            </Text>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <Tooltip label={selectedCampaign.name} disabled={selectedCampaign.name.length < 30}>
                                        <Text size="sm" fw={600} truncate maw={300}>
                                            {selectedCampaign.name}
                                        </Text>
                                    </Tooltip>
                                )}
                            </Breadcrumbs>
                        </Card.Section>
                    )}
                    <Card.Section
                        inheritPadding
                        py="md"
                        bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))"
                    >
                        <Group gap="md" align="center">
                            <PlatformSelector
                                id="platform"
                                gameId={gameId}
                                value={platform}
                                onChange={handlePlatformChange}
                                w={180}
                            />
                            <SegmentedControl
                                value={activeTab}
                                onChange={handleTabChange}
                                size="sm"
                                data={[
                                    { label: 'Campaigns', value: 'campaigns', disabled: !!selectedCampaign },
                                    { label: 'Ad Groups', value: 'adGroups', disabled: !!selectedAdGroup },
                                    { label: 'Ads', value: 'ads' },
                                ]}
                            />
                        </Group>
                    </Card.Section>
                    <Card.Section inheritPadding py="md">
                        {activeTab === 'campaigns' && visitedTabs.has('campaigns') && platform && (
                            <CampaignsTabContent
                                gameId={gameId}
                                platform={platform}
                                onDataLoaded={setCampaignsData}
                                getCampaignUrl={getCampaignUrl}
                                onCampaignClick={handleCampaignClick}
                            />
                        )}
                        {activeTab === 'adGroups' && visitedTabs.has('adGroups') && platform && (
                            <AdGroupsTabContent
                                key={campaignIdFromUrl ?? 'all'}
                                gameId={gameId}
                                platform={platform}
                                campaignId={campaignIdFromUrl ?? undefined}
                                onDataLoaded={setAdGroupsData}
                                selectedCampaign={selectedCampaign}
                                getAdGroupUrl={getAdGroupUrl}
                                onAdGroupClick={handleAdGroupClick}
                            />
                        )}
                        {activeTab === 'ads' && visitedTabs.has('ads') && platform && (
                            <AdsTabContent
                                key={`${campaignIdFromUrl ?? 'all'}-${adGroupIdFromUrl ?? 'all'}`}
                                gameId={gameId}
                                platform={platform}
                                campaignId={campaignIdFromUrl ?? undefined}
                                adGroupId={adGroupIdFromUrl ?? undefined}
                                selectedCampaign={selectedCampaign}
                                selectedAdGroup={selectedAdGroup}
                                game={game}
                            />
                        )}
                    </Card.Section>
                </Card>
            </Stack>
        </PageWide>
    );
};
